import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Boxes,
  Bot,
  Cable,
  ChevronRight,
  FileClock,
  FolderCog,
  GitBranch,
  Grid2x2,
  HandCoins,
  Home,
  KeyRound,
  LibraryBig,
  ListChecks,
  Route,
  Settings,
  ShieldCheck,
  Sparkles,
  BrainCircuit,
  Network,
  UserCircle2,
  Workflow,
  Activity,
} from 'lucide-react';
import Sidebar from './Sidebar';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ToastProvider } from './Toast';
import { ThemeProvider, useTheme } from './Theme';

const SEGMENT_LABELS: Record<string, string> = {
  home: 'Home',
  workflows: 'Workflows',
  orchestration: 'Agent Orchestrations',
  reports: 'Reports',
  'analytics-hub': 'Analytics Hub',
  designer: 'Report Designer',
  'reports-builder': 'Analytics Hub',
  configurations: 'Configurations',
  keys: 'Keys',
  'data-ingestion': 'Data Ingestion',
  'llm-routing': 'LLM Routing',
  'data-vault': 'Data Vault',
  schedules: 'Schedules',
  'knowledge-base': 'Knowledge Base',
  logs: 'AI Observability',
  'health-status': 'System Health',
  'my-account': 'My Account',
  'access-settings': 'Access Settings',
  connections: 'Connections',
  flowbuilder: 'Flow Builder',
  deployments: 'Deployments',
  agents: 'Agents',
  'system-settings': 'System Settings',
  agentStudio: 'Agent Studio',
  applications: 'Applications',
  catalog: 'Catalog',
  'ai-models': 'AI Models',
  register: 'Register',
  'intent-orchestration': 'Intent Orchestration',
  chatagent: 'Chat Agents',
  createchatagent: 'Create Chat Agent',
  mcpresources: 'External MCPs',
  mcplivemonitor: 'MCP Live Monitor',
  'governed-mcp-gateway': 'AI Gateway Fabric',
  'ai-gateway': 'AI Gateway Fabric',
  llm: 'LLM Gateway',
  new: 'Create',
  edit: 'Edit',
  dashboard: 'Dashboard',
  tools: 'Tools',
  configuration: 'Configuration',
  'ai-usage': 'AI Usage & Spendings',
  chatbot: 'Chatbot',
  'ai-governance': 'AI Governance',
  policies: 'Policies',
  groups: 'Policy Groups',
  assignments: 'Assignments',
  overrides: 'Overrides',
  library: 'Policy Library',
  'audit-logs': 'Audit Logs',
};

const prettifySegment = (segment: string): string => {
  if (!segment) return '';
  const known = SEGMENT_LABELS[segment];
  if (known) return known;
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const SEGMENT_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  home: Home,
  workflows: Workflow,
  orchestration: Workflow,
  reports: ListChecks,
  'analytics-hub': ListChecks,
  'reports-builder': ListChecks,
  configurations: FolderCog,
  keys: KeyRound,
  'data-ingestion': Boxes,
  'llm-routing': Route,
  'data-vault': LibraryBig,
  schedules: FileClock,
  'knowledge-base': LibraryBig,
  logs: FileClock,
  'health-status': Activity,
  'my-account': UserCircle2,
  'access-settings': ShieldCheck,
  connections: Cable,
  flowbuilder: GitBranch,
  deployments: HandCoins,
  agents: Bot,
  'system-settings': Settings,
  agentStudio: Sparkles,
  applications: Grid2x2,
  'ai-models': BrainCircuit,
  'intent-orchestration': GitBranch,
  chatagent: Bot,
  createchatagent: Bot,
  chatbot: Bot,
  'governed-mcp-gateway': ShieldCheck,
  'ai-gateway': Network,
  llm: Network,
};

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'portal_sidebar_collapsed';

type PortalLayoutContextValue = {
  setSidebarCollapsed: (collapsed: boolean) => void;
};

const PortalLayoutContext = createContext<PortalLayoutContextValue | null>(null);

export function usePortalLayout(): PortalLayoutContextValue {
  const context = useContext(PortalLayoutContext);
  if (!context) {
    throw new Error('usePortalLayout must be used within PortalLayout');
  }
  return context;
}

function readSidebarCollapsedFromStorage(): boolean {
  try {
    const raw = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (raw === null) return false;
    return raw === 'true';
  } catch {
    return false;
  }
}

const PortalLayoutContent = () => {
  const [collapsed, setCollapsed] = useState(readSidebarCollapsedFromStorage);
  const sidebarBeforeDesignerRef = useRef<boolean | null>(null);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  }, [collapsed]);
  const { theme } = useTheme();
  const location = useLocation();
  const isFlowBuilderRoute = location.pathname.includes('/flowbuilder');
  const isReportDesignerRoute = location.pathname.includes('/analytics-hub/designer');
  const isOrchestrationRoute = location.pathname.startsWith('/portal/orchestration');
  const isFullBleedRoute = isFlowBuilderRoute || isReportDesignerRoute || isOrchestrationRoute;

  useEffect(() => {
    if (isReportDesignerRoute) {
      if (sidebarBeforeDesignerRef.current === null) {
        sidebarBeforeDesignerRef.current = collapsed;
      }
      setCollapsed(true);
      return;
    }

    if (sidebarBeforeDesignerRef.current !== null) {
      setCollapsed(sidebarBeforeDesignerRef.current);
      sidebarBeforeDesignerRef.current = null;
    }
  }, [isReportDesignerRoute]);
  const isLightTheme = theme === 'white';
  const layoutContextValue = useMemo(() => ({ setSidebarCollapsed: setCollapsed }), []);
  const breadcrumbItems = useMemo(() => {
    const relativePath = location.pathname.replace(/^\/portal\/?/, '');
    if (!relativePath || relativePath === 'home') {
      return [{ label: 'Home', href: '/portal/home', isLast: true }];
    }

    const rawSegments = relativePath.split('/').filter(Boolean);
    const segments = rawSegments.map((segment, index) => {
      const isIdSegment =
        index > 0 &&
        rawSegments[index - 1] === 'chatbot' &&
        segment.length > 0;
      return {
        segment,
        label: isIdSegment ? 'Edit Chat Agent' : prettifySegment(segment),
      };
    });

    return [{ label: 'Home', href: '/portal/home', isLast: segments.length === 0 }].concat(
      segments.map((item, index) => ({
        label: item.label,
        href: `/portal/${rawSegments.slice(0, index + 1).join('/')}`,
        isLast: index === segments.length - 1,
      }))
    );
  }, [location.pathname]);

  return (
    <ToastProvider>
      <div className={`portal-layout flex min-h-screen transition-[padding-left] duration-200 ${isLightTheme ? 'bg-white text-neutral-900' : 'bg-neutral-950 text-white'} ${collapsed ? 'pl-12' : 'pl-52'}`}>
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        <main
          className={
            isReportDesignerRoute
              ? 'flex-1 flex flex-col min-h-0 h-screen overflow-hidden pl-1.5 pt-1 pr-1 pb-1'
              : isFullBleedRoute
              ? 'flex-1 flex flex-col min-h-0 h-screen overflow-hidden p-0'
              : 'flex-1 min-w-0 p-3 overflow-auto min-h-screen flex flex-col'
          }
        >
          <div
            className={
              isReportDesignerRoute
                ? 'flex flex-1 flex-col min-h-0 w-full overflow-hidden rounded-md'
                : isFullBleedRoute
                ? 'flex flex-1 flex-col min-h-0 w-full overflow-hidden'
                : 'portal-content flex-1 flex flex-col min-h-0 w-full min-w-0 max-w-none overflow-x-auto'
            }
          >
            <PortalLayoutContext.Provider value={layoutContextValue}>
              <Outlet />
            </PortalLayoutContext.Provider>
          </div>
        </main>
      </div>
    </ToastProvider>
  );
};

const PortalLayout = () => {
  return (
    <ThemeProvider>
      <PortalLayoutContent />
    </ThemeProvider>
  );
};

export default PortalLayout;
